import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref, watch, type Ref } from 'vue';
import CButton from '@/components/shared/CButton';
import CFileInput, { type CFileInputRef } from '@/components/shared/CFileInput';
import CPreview from '@/components/shared/CPreview.vue';
import MaskSelectorModal from './MaskSelectorModal.vue';
import type { MaskDto } from '@/api/typings/mask';
import {
    MASK_UPLOAD_ACCEPT,
    RASTER_MASK_UPLOAD_ACCEPT,
    deriveMaskThumbnailPath,
    maskNameFromPath,
    uploadAndRegisterMask
} from '@/utils/maskUtils';


export default /*#__PURE__*/_defineComponent({
  __name: 'MaskProperties',
  props: {
    maskPath: null,
    disabled: { type: Boolean },
    disableSvg: { type: Boolean },
    mixed: { type: Boolean }
  },
  emits: ["select-mask", "remove-mask", "error"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'select-mask', mask: MaskDto): void;
    (event: 'remove-mask'): void;
    (event: 'error', message: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    maskPath?: string;
    disabled?: boolean;
    disableSvg?: boolean;
    mixed?: boolean;
};





const model = reactive({
    modalOpen: false,
    uploading: false,
    pickedMask: undefined as MaskDto | undefined
});

const fileInputRef: Ref<CFileInputRef | undefined> = ref();

const hasMask = computed(() => !!props.maskPath || props.mixed);
const accept = computed(() => (props.disableSvg ? RASTER_MASK_UPLOAD_ACCEPT : MASK_UPLOAD_ACCEPT));

const maskThumbnail = computed(() => {
    if (props.mixed || !props.maskPath) return '';
    return model.pickedMask?.thumbnailPath || deriveMaskThumbnailPath(props.maskPath);
});

const maskName = computed(() => {
    if (props.mixed) return 'Mixed';
    if (!props.maskPath) return '';
    return model.pickedMask?.name || maskNameFromPath(props.maskPath);
});

// Masks applied before this feature existed have no generated thumbnail, fall back to the mask itself
const previewSource = ref('');
watch(
    maskThumbnail,
    (thumbnail) => {
        previewSource.value = thumbnail;
    },
    { immediate: true }
);
const onPreviewFailed = () => {
    if (props.maskPath && previewSource.value !== props.maskPath) {
        previewSource.value = props.maskPath;
    }
};

watch(
    () => props.maskPath,
    (maskPath) => {
        if (!maskPath || maskPath !== model.pickedMask?.path) {
            model.pickedMask = undefined;
        }
    }
);

const openModal = () => {
    if (props.disabled) return;
    model.modalOpen = true;
};

const openFileUploader = () => {
    if (props.disabled) return;
    fileInputRef.value?.openFileUploader();
};

const onMaskSelected = (mask: MaskDto) => {
    model.modalOpen = false;
    model.pickedMask = mask;
    emit('select-mask', mask);
};

const onFileUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    model.uploading = true;
    try {
        onMaskSelected(await uploadAndRegisterMask(file));
    } catch (error) {
        emit('error', (error as Error).message || 'The mask could not be uploaded');
    } finally {
        model.uploading = false;
    }
};

return { __sfc: true,props, emit, model, fileInputRef, hasMask, accept, maskThumbnail, maskName, previewSource, onPreviewFailed, openModal, openFileUploader, onMaskSelected, onFileUpload, CButton, CFileInput, CPreview, MaskSelectorModal }
}

})